import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import HistoryIcon from '@mui/icons-material/History';
import PeopleIcon from '@mui/icons-material/People';
import { useNavigate } from 'react-router-dom';
import { useAuthorizationsList, useMachinesList, useMe, useMyPermissions, useOperatorsList, useSitesList, useUsersList } from '../api/queries';
import { useQuery } from '@tanstack/react-query';
import { usePageTitle } from '../components/shell/AppShell';
import type { ReactNode } from 'react';

/**
 * Homer-inspired tile dashboard. Each tile is permission-gated; tiles the
 * user can't see are not rendered. Counts come from the same APIs the
 * downstream pages consume — TanStack Query caches them so a click-through
 * doesn't re-fetch.
 */
export default function HomePage() {
  usePageTitle('Home');
  const navigate = useNavigate();

  const me = useMe();
  const perms = useMyPermissions();

  // Operators + Users tiles — fetch the lists to show counts. Cheap; same
  // queries the downstream pages use, so the click-through is instant.
  const operators = useOperatorsList();
  const operatorsList = useQuery({ queryKey: operators.queryKey, queryFn: operators.listFn });
  const users = useUsersList();
  const usersList = useQuery({ queryKey: users.queryKey, queryFn: users.listFn });
  // Sites tile — for ACES users this counts every operator's sites; for
  // operator users RLS scopes the result to their own.
  const sites = useSitesList();
  const sitesList = useQuery({ queryKey: sites.queryKey, queryFn: sites.listFn });
  // Active machines tile — operator users see their fleet; ACES sees all.
  const machines = useMachinesList({ status: 'active' });
  const machinesList = useQuery({ queryKey: machines.queryKey, queryFn: machines.listFn });
  const pendingAuth = useAuthorizationsList('pending');
  const pendingAuthList = useQuery({ queryKey: pendingAuth.queryKey, queryFn: pendingAuth.listFn });

  // RequireAuth (App.tsx) gates this route on a session token. Mid-
  // session expiry is handled by the global 401 handler in apiFetch
  // — token cleared, browser bounced to /login. So if we reach this
  // function body the token exists; me.isError just means the load is
  // mid-flight or temporarily flaky.
  const greetingName = me.data?.display_name?.trim() || me.data?.username || '…';
  const has = (code: string) => perms.data?.codes?.includes(code) ?? false;

  return (
    <>
      <Stack spacing={4}>
        <Box>
          <Typography variant="h4" data-testid="greeting">Hello, {greetingName}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
            {me.data?.impersonated_operator_id ? (
              // While impersonating, ARMS is dropped: show the operator the
              // actor is acting as plus the role they assumed. Never "ARMS".
              <>
                <Chip
                  label={
                    me.data.impersonated_operator_display_name
                      ?? me.data.impersonated_operator_code
                      ?? `${me.data.impersonated_operator_id.slice(0, 8)}…`
                  }
                  variant="outlined" size="small"
                />
                <Chip
                  label={
                    me.data.impersonated_role_display_name
                      ?? me.data.impersonated_role_code
                      ?? 'Role'
                  }
                  color="secondary" size="small"
                />
              </>
            ) : (
              <>
                {me.data?.is_arms && <Chip label="ARMS" color="secondary" size="small" />}
                {me.data?.operator_id && (
                  <Chip
                    label={
                      me.data.operator_display_name
                        ?? me.data.operator_code
                        ?? `${me.data.operator_id.slice(0, 8)}…`
                    }
                    variant="outlined" size="small"
                  />
                )}
              </>
            )}
          </Stack>
        </Box>

        {/* Tile dashboard */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
        }}>
          {has('platform.operators.read') && (
            <Tile
              title="Operators"
              icon={<BusinessIcon />}
              value={operatorsList.isLoading ? '…' : (operatorsList.data?.length ?? 0)}
              subtitle="Tenants on the platform"
              onClick={() => navigate('/operators')}
            />
          )}
          {has('sites.view') && (
            <Tile
              title="Sites"
              icon={<StorefrontIcon />}
              value={sitesList.isLoading ? '…' : (sitesList.data?.length ?? 0)}
              subtitle="Locations on the platform"
              onClick={() => navigate('/sites')}
            />
          )}
          {has('users.view') && (
            <Tile
              title="Users"
              icon={<PeopleIcon />}
              value={usersList.isLoading ? '…' : (usersList.data?.length ?? 0)}
              subtitle="People with portal access"
              onClick={() => navigate('/users')}
            />
          )}
          {has('authorizations.view') && (
            <Tile
              title="Pending approvals"
              icon={<HowToVoteIcon />}
              value={pendingAuthList.isLoading ? '…' : (pendingAuthList.data?.length ?? 0)}
              subtitle="Authorization requests waiting"
              onClick={() => navigate('/approvals')}
            />
          )}
          {has('machines.view') && (
            <Tile
              title="Active machines"
              icon={<VideogameAssetIcon />}
              value={machinesList.isLoading ? '…' : (machinesList.data?.length ?? 0)}
              subtitle="On the floor right now"
              onClick={() => navigate('/machines')}
            />
          )}
          {has('audit.view') && (
            <Tile
              title="Audit log"
              icon={<HistoryIcon />}
              value="→"
              subtitle="Sign-in events + activity"
              onClick={() => navigate('/audit')}
            />
          )}
        </Box>
      </Stack>
    </>
  );
}

type TileProps = {
  title: string;
  icon: ReactNode;
  value: number | string;
  subtitle?: string;
  onClick?: () => void;
  disabled?: boolean;
};

function Tile({ title, icon, value, subtitle, onClick, disabled }: TileProps) {
  const inner = (
    <Stack spacing={1} sx={{
      p: 2.5, height: '100%', position: 'relative',
      // Top stripe in the user's accent colour. Removes when disabled.
      '&::before': disabled ? undefined : {
        content: '""', position: 'absolute', left: 0, right: 0, top: 0,
        height: 4, bgcolor: 'secondary.main',
        borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit',
      },
    }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        color: disabled ? 'text.disabled' : 'secondary.main',
      }}>
        {icon}
        <Typography variant="overline" color={disabled ? 'text.disabled' : 'text.secondary'}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h3" sx={{ lineHeight: 1.1, fontWeight: 600,
        color: disabled ? 'text.disabled' : 'text.primary' }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
      )}
    </Stack>
  );

  if (onClick && !disabled) {
    return (
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
          {inner}
        </CardActionArea>
      </Card>
    );
  }
  return <Card variant="outlined" sx={{ height: '100%' }}>{inner}</Card>;
}
